import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import { Calendar, CircleDot, GitFork } from "lucide-react";
import { Badge } from "../../ui/badge";

import type { GithubProjectItem } from "@cohesive/api-entities";
import {
  BaseEntityCard,
  BaseEntityCardActions,
  BaseEntityCardBody,
  BaseEntityCardContent,
  BaseEntityCardContextMenu,
  BaseEntityCardDescription,
  BaseEntityCardExternalLinkAction,
  BaseEntityCardHeader,
  BaseEntityCardIcon,
  BaseEntityCardMentionAction,
  BaseEntityCardMetadata,
  BaseEntityCardMetadataItem,
  BaseEntityCardProps,
  BaseEntityCardTags,
  BaseEntityCardTitle,
} from "../BaseCard";

export { GithubProjectItem as ProjectItem };

type ProjectItemCardProps = GithubProjectItem & BaseEntityCardProps;

export function ProjectItemCardAvatar({
  content_state,
  className,
  style,
}: Partial<ProjectItemCardProps>) {
  const state = content_state?.toUpperCase();
  const color =
    state === "OPEN"
      ? "text-lc-active"
      : state === "MERGED"
        ? "text-plum"
        : "text-lc-blocked";

  return (
    <BaseEntityCardIcon className={className} style={style}>
      <CircleDot className={cn("h-5 w-5", color)} />
    </BaseEntityCardIcon>
  );
}

export function ProjectItemCard({
  platform,
  content_title,
  content_body = "",
  content_state,
  content_url,
  repository_name,
  repository_owner,
  updated_at,
  onClick,
  onDoubleClick,
  onMentionClick,
  actions,
  processes,
  className,
  style,
  web_link,
}: ProjectItemCardProps) {
  const formattedUpdatedDate = updated_at
    ? formatDistanceToNow(new Date(updated_at), { addSuffix: true })
    : "";

  const descriptionPreview =
    content_body && content_body.length > 100
      ? `${content_body.substring(0, 100)}...`
      : content_body;

  // Get state color - subtle text-only styling like detail view
  const getStateColor = () => {
    const state = content_state?.toUpperCase();
    if (state === "OPEN")
      return "text-lc-active dark:text-lc-active border-green-600/20";
    if (state === "CLOSED")
      return "text-lc-blocked dark:text-lc-blocked border-red-600/20";
    if (state === "MERGED")
      return "text-plum dark:text-plum border-purple-600/20";
    return "text-muted-foreground";
  };

  const getStateLabel = () => {
    if (!content_state) return null;
    const state = content_state.toUpperCase();
    if (state === "OPEN") return "Open";
    if (state === "CLOSED") return "Closed";
    if (state === "MERGED") return "Merged";
    return content_state;
  };

  return (
    <BaseEntityCard
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      processes={processes}
      className={className}
      style={style}
    >
      <ProjectItemCardAvatar content_state={content_state} />

      <BaseEntityCardContent>
        <BaseEntityCardHeader>
          <BaseEntityCardTitle platform={platform}>
            {content_title || "Untitled Item"}
          </BaseEntityCardTitle>
          <BaseEntityCardActions>
            {(web_link || content_url) && (
              <BaseEntityCardExternalLinkAction
                href={web_link || content_url || "#"}
                tooltip="Open in GitHub"
              />
            )}
            {onMentionClick && (
              <BaseEntityCardMentionAction onClick={onMentionClick} />
            )}
            {actions && actions.length > 0 && (
              <BaseEntityCardContextMenu actions={actions} />
            )}
          </BaseEntityCardActions>
        </BaseEntityCardHeader>

        <BaseEntityCardBody>
          {descriptionPreview && (
            <BaseEntityCardDescription className="line-clamp-2 text-foreground">
              {descriptionPreview}
            </BaseEntityCardDescription>
          )}

          <BaseEntityCardTags>
            {getStateLabel() && (
              <Badge
                variant="outline"
                className={cn("text-xs", getStateColor())}
              >
                {getStateLabel()}
              </Badge>
            )}

            {repository_name && repository_owner && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <GitFork className="h-3 w-3" />
                <span>
                  {repository_owner}/{repository_name}
                </span>
              </div>
            )}
          </BaseEntityCardTags>

          {formattedUpdatedDate && (
            <BaseEntityCardMetadata>
              <BaseEntityCardMetadataItem
                icon={<Calendar className="h-3 w-3" />}
              >
                Updated {formattedUpdatedDate}
              </BaseEntityCardMetadataItem>
            </BaseEntityCardMetadata>
          )}
        </BaseEntityCardBody>
      </BaseEntityCardContent>
    </BaseEntityCard>
  );
}
